import Header from "@/shared/components/ui/Header";
import MainLayout from "@/shared/components/ui/MainLayout";
import { Printer } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface SerialSettings {
    prefix: string;
    startNum: number;
    endNum: number;
}

const CreateSerialLabels = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // تعديل الحالة الافتراضية لتشمل البداية والنهاية بدلاً من العدد الإجمالي
    const [settings, setSettings] = useState<SerialSettings>({
        prefix: 'LBL',
        startNum: 1,
        endNum: 10
    });

    const handleInputChange = (field: keyof SerialSettings, value: string | number) => {
        setSettings(prev => ({
            ...prev,
            [field]: field === 'prefix' ? value : (Number(value) || 0)
        }));
    };

    const handleGenerateAndPrint = () => {
        const { prefix, startNum, endNum } = settings;

        if (startNum > endNum) {
            alert(t("رقم البداية يجب أن يكون أصغر من أو يساوي رقم النهاية"));
            return;
        }

        const generatedRows = [];
        
        const maxLength = String(endNum).length;

        for (let i = startNum; i <= endNum; i++) {
            const paddedNumber = String(i).padStart(maxLength, '0');
            const serial = `${prefix}${paddedNumber}`;

            generatedRows.push({
                id: Date.now() + i,
                name: serial,
                qty: 1,
                barcode: serial
            });
        }

        if (generatedRows.length > 0) {
            localStorage.setItem('serial_labels', JSON.stringify(generatedRows));
            navigate('/print_labels?serial_labels=serial_labels');
        }
    };

    return (
        <MainLayout>
            <Header title={`${t('Create Serial Labels')}`} info={``} />

            <div className="mt-4" dir="rtl">
                <div className="max-w-xl mx-auto">
                    <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">{t("Generate Serial Numbers")}</h1>
                        </div>
                    </header>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-6">
                        <div className="space-y-1">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                {t("Prefix (e.g., LBL)")}
                            </label>
                            <input
                                type="text"
                                value={settings.prefix}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('prefix', e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all font-mono text-lg"
                                placeholder="LBL"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    {t("Start Number")}
                                </label>
                                <input
                                    type="number"
                                    value={settings.startNum}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('startNum', e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all font-mono text-lg"
                                    placeholder="1"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    {t("End Number")}
                                </label>
                                <input
                                    type="number"
                                    value={settings.endNum}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('endNum', e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all font-mono text-lg"
                                    placeholder="100"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleGenerateAndPrint}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-300 font-medium hover:bg-slate-50 transition-all">
                        
                            <Printer className="w-5 h-5" />
                              {t("Print Labels")}
                        </button>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default CreateSerialLabels;